import React, { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { apiService } from '../services/api';
import { AuthContextType, User } from '../types';
import { safeStorage } from '../utils/safeStorage';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

// Helper to extract error messages safely
const getErrorMessage = (error: unknown): string => {
  if (error && typeof error === 'object' && 'response' in error) {
    const response = (error as { response?: { data?: { detail?: string } } }).response;
    if (response?.data?.detail) {
      return response.data.detail;
    }
  }
  return 'An error occurred';
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    initializeAuth();

    const handleTokenCleared = () => {
      setUser(null);
      setToken(null);
    };

    window.addEventListener('auth:token-cleared', handleTokenCleared);

    return () => {
      window.removeEventListener('auth:token-cleared', handleTokenCleared);
    };
  }, []);

  const initializeAuth = async () => {
    try {
      const storedToken = safeStorage.getItem('access_token');
      if (storedToken) {
        setToken(storedToken);
        const userData = await apiService.getCurrentUser();
        setUser(userData);
      }
    } catch {
      // Token is invalid or expired – wipe everything so ProtectedRoute redirects
      apiService.logout();
      setToken(null);
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (email: string, password: string): Promise<void> => {
    try {
      setIsLoading(true);
      const authData = await apiService.login(email, password);

      // Get user data before setting auth state to avoid half-authenticated renders
      const userData = await apiService.getCurrentUser();

      setToken(authData.access_token);
      setUser(userData);

      toast.success('Successfully logged in!');
    } catch (error: unknown) {
      // Ensure fully logged out on any failure
      apiService.logout();
      setToken(null);
      setUser(null);

      const message = getErrorMessage(error) || 'Login failed';
      toast.error(message);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    apiService.logout();
    setUser(null);
    setToken(null);
    toast.success('Successfully logged out');
  };

  const refreshToken = async (): Promise<void> => {
    try {
      const authData = await apiService.refreshToken();
      setToken(authData.access_token);
    } catch (error) {
      // Refresh failed, logout user
      logout();
      throw error;
    }
  };

  const contextValue: AuthContextType = {
    user,
    token,
    login,
    logout,
    refreshToken,
    isLoading,
    isAuthenticated: !!user && !!token,
  };

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
